import CryptoJS from "crypto-js";
import Cookies from "js-cookie";

const API_BASE_URL = process.env.REACT_APP_LOCAL_API_URL;

const COOKIE_NAME = "osteomed_session";
const SECRET = process.env.REACT_APP_COOKIE_SECRET || "osteomed_dev_key";

// Match UserContext's session restoration order. A stale/missing cookie must
// not make API requests use a different session from the logged-in UI.
function getAuthHeaders() {
    const sources = [
        () => localStorage.getItem("osteomed_user_data"),
        () => Cookies.get(COOKIE_NAME),
    ];
    for (const read of sources) {
        try {
            const cipher = read();
            if (!cipher) continue;
            const bytes = CryptoJS.AES.decrypt(cipher, SECRET);
            const data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            if (typeof data?.token === "string" && data.token.trim()) {
                return { Authorization: `Bearer ${data.token}` };
            }
        } catch (_) {
            // Try the legacy cookie when storage is unavailable or invalid.
        }
    }
    return {};
}

const request = async (url, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        // Evita 304 sem corpo: Express envia ETag e o fetch não preenche .json() a partir do cache.
        cache: options.cache ?? "no-store",
        headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
            ...options.headers,
        },
    });
    if (!response.ok) {
        if (response.status === 401 || response.status === 402) {
            window.dispatchEvent(new Event("auth:unauthorized"));
        }
        if (response.status >= 500) {
            window.dispatchEvent(new CustomEvent("app:server-error"));
        }
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || "Erro na requisição";
        const error = new Error(message);
        error.status = response.status;
        error.code = errorData.code;
        error.erros = errorData.erros;
        error.data = errorData;
        throw error;
    }
    return response.json();
};

// Igual a `request`, mas para envio de FormData (multipart) — não força Content-Type,
// que precisa ficar a cargo do fetch nativo pra incluir o boundary corretamente.
const requestMultipart = async (url, formData, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
        method: "POST",
        ...options,
        cache: "no-store",
        headers: { ...getAuthHeaders(), ...options.headers },
        body: formData,
    });
    if (!response.ok) {
        if (response.status === 401 || response.status === 402) {
            window.dispatchEvent(new Event("auth:unauthorized"));
        }
        if (response.status >= 500) {
            window.dispatchEvent(new CustomEvent("app:server-error"));
        }
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || "Erro na requisição";
        const error = new Error(message);
        error.status = response.status;
        error.code = errorData.code;
        error.erros = errorData.erros;
        error.data = errorData;
        throw error;
    }
    return response.json();
};

const fetchEspecialidades = async (clinicaId) => {
    return fetch(`${API_BASE_URL}api/adm/especialidades/clinica/${clinicaId}`, {
        cache: "no-store",
        headers: { ...getAuthHeaders() },
    }).then((res) => (res.ok ? res.json() : []));
};

const fetchAllPacientes = async (clinicaId) => {
    return fetch(`${API_BASE_URL}api/paciente/clinica/${clinicaId}?limit=9999`, {
        cache: "no-store",
        headers: { ...getAuthHeaders() },
    }).then((res) => (res.ok ? res.json().then((d) => (Array.isArray(d) ? d : d.pacientes ?? [])) : []));
};


class uploadFoto {
    static criar = async (photoFile) => {
        const formData = new FormData();
        formData.append("photo", photoFile);

        const response = await fetch(`${API_BASE_URL}api/foto/upload`, {
            method: "POST",
            headers: { ...getAuthHeaders() },
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const message = errorData.message || "Falha ao fazer upload da foto";
            const error = new Error(message);
            error.status = response.status;
            throw error;
        }

        const data = await response.json();
        return data.foto._id;
    };

    static atualizar = async (id, photoFile) => {
        const formData = new FormData();
        formData.append("photo", photoFile);

        const response = await fetch(`${API_BASE_URL}api/foto/upload/${id}`, {
            method: "PUT",
            headers: { ...getAuthHeaders() },
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const message = errorData.message || "Falha ao fazer upload da foto";
            const error = new Error(message);
            error.status = response.status;
            throw error;
        }

        const data = await response.json();
        return data.foto._id;
    };

    static deletar = async (id) => {
        await request(`api/foto/upload/${id}`, {
            method: "DELETE",
            headers: { ...getAuthHeaders() },
        });
        return null;
    };
}

export { fetchEspecialidades, fetchAllPacientes, request, requestMultipart, getAuthHeaders, uploadFoto }
